/**
 * Custom shadows configuration for AgentOps modern theme.
 * Includes glow effects for primary/accent colors.
 */

// Type Imports
import type { SystemMode } from '../@core/types';

/**
 * Custom shadows type used by our theme system.
 */
export type CustomShadows = {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  primary: { sm: string; md: string; lg: string };
  secondary: { sm: string; md: string; lg: string };
  error: { sm: string; md: string; lg: string };
  warning: { sm: string; md: string; lg: string };
  info: { sm: string; md: string; lg: string };
  success: { sm: string; md: string; lg: string };
};

/**
 * Custom shadows configuration function
 * @param mode - System mode (light or dark)
 * @returns Custom shadows object
 */
const customShadows = (mode: SystemMode): CustomShadows => {
  const isDark = mode === 'dark';

  return {
    xs: isDark
      ? '0 0 0 1px rgba(255, 255, 255, 0.04), 0 1px 2px rgba(0, 0, 0, 0.3)'
      : '0 1px 2px rgba(0, 0, 0, 0.06)',
    sm: isDark
      ? '0 0 0 1px rgba(255, 255, 255, 0.06), 0 2px 8px rgba(0, 0, 0, 0.3)'
      : '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
    md: isDark
      ? '0 0 0 1px rgba(255, 255, 255, 0.06), 0 4px 16px rgba(0, 0, 0, 0.35)'
      : '0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -2px rgba(0, 0, 0, 0.06)',
    lg: isDark
      ? '0 0 0 1px rgba(255, 255, 255, 0.06), 0 8px 24px rgba(0, 0, 0, 0.4)'
      : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.04)',
    xl: isDark
      ? '0 0 0 1px rgba(255, 255, 255, 0.08), 0 16px 48px rgba(0, 0, 0, 0.5)'
      : '0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)',
    primary: {
      sm: isDark
        ? '0 0 8px rgba(108, 63, 192, 0.25)'
        : '0 2px 6px rgba(108, 63, 192, 0.2)',
      md: isDark
        ? '0 0 16px rgba(108, 63, 192, 0.3), 0 4px 12px rgba(108, 63, 192, 0.15)'
        : '0 4px 12px rgba(108, 63, 192, 0.25)',
      lg: isDark
        ? '0 0 24px rgba(108, 63, 192, 0.35), 0 8px 20px rgba(108, 63, 192, 0.2)'
        : '0 6px 20px rgba(108, 63, 192, 0.3)',
    },
    secondary: {
      sm: isDark
        ? '0 0 8px rgba(148, 163, 184, 0.15)'
        : '0 2px 6px rgba(100, 116, 139, 0.2)',
      md: isDark
        ? '0 0 16px rgba(148, 163, 184, 0.2)'
        : '0 4px 12px rgba(100, 116, 139, 0.25)',
      lg: isDark
        ? '0 0 24px rgba(148, 163, 184, 0.25)'
        : '0 6px 20px rgba(100, 116, 139, 0.3)',
    },
    error: {
      sm: isDark
        ? '0 0 8px rgba(248, 113, 113, 0.25)'
        : '0 2px 6px rgba(239, 68, 68, 0.2)',
      md: isDark
        ? '0 0 16px rgba(248, 113, 113, 0.3)'
        : '0 4px 12px rgba(239, 68, 68, 0.25)',
      lg: isDark
        ? '0 0 24px rgba(248, 113, 113, 0.35)'
        : '0 6px 20px rgba(239, 68, 68, 0.3)',
    },
    warning: {
      sm: isDark
        ? '0 0 8px rgba(251, 191, 36, 0.25)'
        : '0 2px 6px rgba(245, 158, 11, 0.2)',
      md: isDark
        ? '0 0 16px rgba(251, 191, 36, 0.3)'
        : '0 4px 12px rgba(245, 158, 11, 0.25)',
      lg: isDark
        ? '0 0 24px rgba(251, 191, 36, 0.35)'
        : '0 6px 20px rgba(245, 158, 11, 0.3)',
    },
    info: {
      sm: isDark
        ? '0 0 8px rgba(34, 211, 238, 0.25)'
        : '0 2px 6px rgba(6, 182, 212, 0.2)',
      md: isDark
        ? '0 0 16px rgba(34, 211, 238, 0.3)'
        : '0 4px 12px rgba(6, 182, 212, 0.25)',
      lg: isDark
        ? '0 0 24px rgba(34, 211, 238, 0.35)'
        : '0 6px 20px rgba(6, 182, 212, 0.3)',
    },
    success: {
      sm: isDark
        ? '0 0 8px rgba(52, 211, 153, 0.25)'
        : '0 2px 6px rgba(16, 185, 129, 0.2)',
      md: isDark
        ? '0 0 16px rgba(52, 211, 153, 0.3)'
        : '0 4px 12px rgba(16, 185, 129, 0.25)',
      lg: isDark
        ? '0 0 24px rgba(52, 211, 153, 0.35)'
        : '0 6px 20px rgba(16, 185, 129, 0.3)',
    },
  };
};

export default customShadows;
